/**
 * Public franchise-requests API.
 *
 * This endpoint is hosted separately from the admin API, so it uses its own
 * base URL (VITE_FRANCHISE_API_BASE_URL) resolved by getFranchiseApiBaseUrl().
 */

import { getFranchiseApiBaseUrl } from "../config/env";

const parseJsonResponse = async (response) => {
  const contentType = response.headers.get("content-type") || "";
  const text = await response.text();

  const looksLikeHtml =
    text.trim().toLowerCase().startsWith("<!doctype") ||
    (text.trim().startsWith("<") && !contentType.includes("application/json"));

  if (looksLikeHtml) {
    throw new Error(
      "Franchise API is misconfigured: received HTML instead of JSON. Set VITE_FRANCHISE_API_BASE_URL to the franchise API host (including the /api suffix).",
    );
  }

  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error("Invalid response from franchise API");
  }

  if (!response.ok) {
    const msg =
      data.error || data.message || data.errors?.[0]?.msg || "Request failed";
    throw new Error(msg);
  }

  return data.success !== undefined
    ? data.data !== undefined
      ? data.data
      : data
    : data;
};

/**
 * Submit a franchise/partner application.
 *
 * @param {Object} payload
 * @param {string} payload.name           Required
 * @param {string} payload.email          Required (must be unique)
 * @param {string} payload.phone          Required
 * @param {string} [payload.whatsapp]     Optional
 * @param {string} [payload.address]      Optional
 * @param {string} [payload.desired_region] Optional
 */
export const submitFranchiseRequest = async ({
  name,
  email,
  phone,
  whatsapp,
  address,
  desired_region,
}) => {
  const base = getFranchiseApiBaseUrl();
  const response = await fetch(`${base}/public/franchise-requests`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: name?.trim(),
      email: email?.trim(),
      phone: phone?.trim(),
      whatsapp: whatsapp?.trim() || undefined,
      address: address?.trim() || undefined,
      desired_region: desired_region?.trim() || undefined,
    }),
  });
  return parseJsonResponse(response);
};

/**
 * Submit a custom-pricing / sales lead. Shares the franchise API base URL.
 *
 * @param {Object} payload
 * @param {string} payload.company_name Required
 * @param {string} payload.email        Required
 * @param {string} payload.phone        Required
 * @param {string} payload.industry     Required
 * @param {string} payload.location     Required
 * @param {string} [payload.message]    Optional
 * @param {string} [payload.source]     Optional (tracking source)
 */
export const submitLead = async ({
  company_name,
  email,
  phone,
  industry,
  location,
  message,
  source,
}) => {
  const base = getFranchiseApiBaseUrl();
  const response = await fetch(`${base}/public/leads`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      company_name: company_name?.trim(),
      email: email?.trim(),
      phone: phone?.trim(),
      industry,
      location: location?.trim(),
      message: message?.trim() || undefined,
      source: source || undefined,
    }),
  });
  return parseJsonResponse(response);
};
